import { useI18n } from "vue-i18n";
import type { Ref } from "vue";
import type { AIChatbotWindow } from "../types/aichatbot";
import globalConfig from "~/composables/globalConfig";
import { getDocumentUrl, LegalDocType } from "~/utils/legalDocs";

interface ChatUserContext {
	id: string | number;
	kname: string;
	vname: string;
	email?: string;
	phone?: {
		fullNumber: string;
	};
}

/**
 * Composable for integrating the Chatlab AI chatbot into the application.
 * @param {string} profile - The profile type for the chatbot, either "default" or "b2b".
 * @returns {object} An object containing methods to interact with the chatbot.
 */
export const useAIChat = (profile = "default") => {
	const { t, locale } = useI18n();

	// Cache translations that are read frequently to avoid repeated lookups.
	const b2bGreeting: string | null = null;

	let intervalId: ReturnType<typeof setInterval> | null = null;
	let b2bAutoOpenTimeoutId: ReturnType<typeof setTimeout> | null = null;

	// Session state controls whether we can safely push user context updates.
	let isSessionActive = false;

	type TrackingPayload = Record<string, string>;

	/**
	 * Push a custom event to the dataLayer for tracking purposes.
	 * @param {string} eventName - The name of the event to push.
	 * @param {TrackingPayload} extra - Additional key-value pairs to include in the event.
	 */
	const pushEvent = (eventName: string, extra: TrackingPayload = {}) => {
		const trackingWindow = window as Window & {
			dataLayer?: Array<Record<string, string>>;
		};

		trackingWindow.dataLayer = trackingWindow.dataLayer || [];
		trackingWindow.dataLayer.push({
			event: eventName,
			chat_provider: "chatlab",
			profile: profile,
			page_path: window.location.pathname,
			page_lang: locale.value,
			...extra,
		});
	};

	/**
	 * Get the greeting text for B2B users.
	 * @returns {string} The translated greeting text.
	 */
	const getB2BGreeting = () => {
		const region = String(globalConfig.region || "hu");
		const lang = String(locale.value || "hu");

		return t("chat.b2b.greeting", {
			aszfUrl: getDocumentUrl(LegalDocType.ASZF, region, lang),
			privacyUrl: getDocumentUrl(LegalDocType.PRIVACY, region, lang),
		});
	};

	/**
	 * Get the bubble message text for B2B users.
	 * @returns {string} The translated bubble message.
	 */
	const getB2BBubbleMessage = () => {
		return t("chat.b2b.bubbleMessage");
	};

	/**
	 * get the Chatlab configuration based on the current profile (default or b2b).
	 * @returns {object} The Chatlab configuration object.
	 */
	const getChatConfig = () => {
		// Support separate Chatlab credentials for B2B and default profiles.
		if (profile === "b2b") {
			return {
				isScriptEnabled: import.meta.env.VITE_CHATLAB_B2B_SCRIPT === "true",
				apiKey: (import.meta.env.VITE_CHATLAB_B2B_API_KEY ?? "") as string,
				providerId: (import.meta.env.VITE_CHATLAB_B2B_PROVIDER_ID ?? "") as string,
			};
		}

		return {
			isScriptEnabled: import.meta.env.VITE_CHATLAB_B2C_SCRIPTT === "true",
			apiKey: (import.meta.env.VITE_CHATLAB_B2C_API_KEY ?? "") as string,
			providerId: (import.meta.env.VITE_CHATLAB_B2C_PROVIDER_ID ?? "") as string,
		};
	};

	const chatConfig = getChatConfig();

	/**
	 * Check if the Chatlab widget is active and ready to use.
	 * @returns {boolean} True if the widget is active, false otherwise.
	 */
	const isActive = () => {
		return chatConfig.isScriptEnabled && !!chatConfig.apiKey && !!chatConfig.providerId;
	};

	/**
	 * Send a message to the AI chatbot.
	 */
	const sendMessage = (message: string) => {
		const chatbotWindow = window as AIChatbotWindow;

		if (!isActive() || !chatbotWindow.aichatbotApi) {
			return;
		}

		try {
			const chatbot = chatbotWindow.aichatbotApi.getChatbotApi(chatConfig.apiKey);
			chatbot.sendMessage(message);
		} catch (error) {
			console.error("Failed to send message to AI:", error);
		}
	};

	/**
	 * Check if the current viewport.
	 */
	const isDesktopViewport = () => {
		return window.matchMedia("(min-width: 740px)").matches;
	};

	/**
	 * Set up a timeout to automatically open the B2B chat widget after a delay.
	 * */
	const setupB2BAutoOpen = () => {
		if (profile !== "b2b" || !isDesktopViewport()) {
			return;
		}

		if (b2bAutoOpenTimeoutId) {
			clearTimeout(b2bAutoOpenTimeoutId);
		}

		b2bAutoOpenTimeoutId = setTimeout(() => {
			if (!isDesktopViewport()) {
				return;
			}

			try {
				const chatbotWindow = window as AIChatbotWindow;
				const chatbot = chatbotWindow.aichatbotApi?.getChatbotApi(chatConfig.apiKey);
				chatbot?.showChat();
			} catch (error) {
				console.error("Failed to auto-open B2B chat:", error);
			}
		}, 8000);
	};

	/**
	 * Initialize the Chatlab widget and set up necessary event listeners.
	 */
	const initChat = (userContextRef: Ref<ChatUserContext | null | undefined>) => {
		const config = chatConfig;
		const chatbotWindow = window as AIChatbotWindow;

		if (!config.isScriptEnabled) {
			return;
		}

		if (!config.apiKey || !config.providerId) return;

		if (!isActive()) return;

		if (chatbotWindow?.aichatbotApi) {
			console.error("Chatlab API is already available on the window object.", profile, config);
			return;
		}

		// Keep replacing dynamic UI text while the widget is rendering/updating.
		setupInterval();

		// Prevent duplicate script injection when init is called multiple times.
		if (document.getElementById(config.providerId)) return;

		setupB2BAutoOpen();

		chatbotWindow.aichatbotApiKey = config.apiKey;
		chatbotWindow.aichatbotProviderId = config.providerId;

		chatbotWindow.aichatbotCallback = {
			onSessionActivated() {
				isSessionActive = true;
				pushEvent("chatlab_session_activated", {
					chat_source: "proactive_message",
				});

				const chatbot = chatbotWindow.aichatbotApi?.getChatbotApi(config.apiKey);
				if (!chatbot) {
					return;
				}

				replaceTextInChat();
				replaceBubbleMessage();
				setupClickListener();

				// Attach user data as soon as the provider session is ready.
				const userContext = userContextRef.value;

				const clientId = userContext?.id ? String(userContext.id) : null;
				const clientName = userContext?.id ? `${userContext.kname} ${userContext.vname}`.trim() : "";

				if (clientId) {
					chatbot.updateClientContext({
						clientId: clientId,
						clientName: clientName,
						clientEmail: userContext?.email || "",
						clientPhone: userContext?.phone?.fullNumber || "",
					});
				}
			},
		};

		const script = document.createElement("script");
		script.src = "https://script.chatlab.com/aichatbot.js";
		script.id = config.providerId;
		script.setAttribute("data-api-key", config.apiKey);
		script.setAttribute("data-provider-id", config.providerId);
		script.defer = true;
		document.head.appendChild(script);
	};

	/**
	 * Set up an interval to repeatedly check for and replace dynamic text in the Chatlab widget.
	 */
	const setupInterval = () => {
		if (intervalId) return;

		intervalId = setInterval(() => {
			replaceTextInChat();
			replaceBubbleMessage();
		}, 100); // Small interval keeps translations in sync with widget DOM updates.
	};

	/**
	 * Set up a click listener to detect when the user clicks the Chatlab launcher button.
	 */
	const setupClickListener = () => {
		document.addEventListener("click", (event) => {
			// `event.target` could be the SVG image inside the button,
			// so we use `.closest()` to check if the click happened anywhere inside the button.
			const launcherButton = (event.target as HTMLElement).closest(".rcw-launcher");

			if (launcherButton) {
				pushEvent("chatlab_proactive_click", {
					chat_source: "proactive_message",
				});

				// The user clicked the default Chatlab button!
				// Chatlab will handle opening the window automatically,
				// we just need to trigger our translation logic.
				replaceTextInChat();
				replaceBubbleMessage();
			}
		});
	};

	/**
	 * Replace the greeting text in the Chatlab widget.
	 */
	const replaceTextInChat = () => {
		const iframe = document.querySelector("#cl-widget") as HTMLIFrameElement;
		if (!iframe?.contentDocument?.body) return;

		const translatedGreeting = profile === "b2b" ? getB2BGreeting() : t("chat.greeting");

		const messageText = iframe.contentDocument?.querySelector(
			".rcw-messages-container .rcw-message .rcw-message-text"
		) as HTMLElement;

		if (messageText) {
			const currentText = messageText.innerText.trim();
			// Update only when needed to avoid unnecessary DOM writes.
			if (
				(currentText === "" || currentText !== translatedGreeting) &&
				messageText.innerHTML !== translatedGreeting
			) {
				messageText.innerHTML = translatedGreeting;
			}
		}
	};

	/**
	 * Replace the bubble message text in the Chatlab widget for B2B users.
	 */
	const replaceBubbleMessage = () => {
		if (profile !== "b2b") return;

		const bubbleMessage = document.querySelector(".chatbot-bubbles-message.show") as HTMLElement;
		if (!bubbleMessage) return;

		const expectedMessage = getB2BBubbleMessage();
		const currentText = (bubbleMessage.textContent || "").trim();
		if (currentText !== expectedMessage) {
			bubbleMessage.textContent = expectedMessage;
		}
	};

	/**
	 * Update the user context in the Chatlab widget.
	 */
	const updateUserContext = (newUser: ChatUserContext) => {
		// Do not update context before Chatlab reports an active session.
		if (!isSessionActive) return;

		const chatbotWindow = window as AIChatbotWindow;

		if (isActive() && newUser && newUser.id && chatbotWindow.aichatbotApi) {
			const chatbot = chatbotWindow.aichatbotApi.getChatbotApi(chatConfig.apiKey);

			const clientId = newUser?.id ? String(newUser.id) : null;
			const clientName = newUser?.id ? `${newUser.kname} ${newUser.vname}`.trim() : "";

			chatbot.updateClientContext({
				clientId: clientId,
				clientName: clientName,
				clientEmail: newUser?.email || "",
				clientPhone: newUser?.phone?.fullNumber || "",
			});
		}
	};

	/**
	 * Tear down the Chatlab integration and clear injected globals/DOM nodes.
	 */
	const destroyChat = () => {
		if (!isActive()) return;

		const config = chatConfig;
		const chatbotWindow = window as AIChatbotWindow;

		if (intervalId) {
			clearInterval(intervalId);
			intervalId = null;
		}

		if (b2bAutoOpenTimeoutId) {
			clearTimeout(b2bAutoOpenTimeoutId);
			b2bAutoOpenTimeoutId = null;
		}

		// Remove the script and chatbot container from the DOM
		const script = document.querySelector(`script[data-api-key="${config.apiKey}"]`);
		if (script) {
			script.remove();
		}

		const aiChatbotContainer = document.querySelector("#aichatbot_ra_div_id");
		if (aiChatbotContainer) {
			aiChatbotContainer.remove();
		}

		delete chatbotWindow.aichatbotApi;
		delete chatbotWindow.aichatbotApiKey;
		delete chatbotWindow.aichatbotProviderId;
		delete chatbotWindow.aichatbotCallback;

		isSessionActive = false;
	};

	return {
		isActive,
		sendMessage,
		initChat,
		updateUserContext,
		destroyChat,
	};
};
